import { useTranslation } from 'react-i18next';

import { Badge, cn, Flex, Skeleton } from '@kognitos/lattice';

import type { TAssetDocumentView } from './asset-views';

/**
 * Props for {@link AssetVersionBadge}.
 */
export interface IAssetVersionBadgeProps {
  value: TAssetDocumentView;
  /** Dims Live for a turned-off asset. */
  isDeactivated?: boolean;
  'data-testid'?: string;
}

/**
 * An asset version's stage badge: green for Live, blue for Draft. The
 * document's title bar shows one while the asset has a single version.
 */
export function AssetVersionBadge({
  value,
  isDeactivated,
  'data-testid': dataTestId = 'asset-version-badge',
}: IAssetVersionBadgeProps) {
  const { t } = useTranslation('common');
  const isLive = value === 'live';

  return (
    <Badge
      variant={isLive ? 'success' : 'secondary'}
      className={cn(
        'ml-1',
        // TODO: Use `variant="informative"` once kognitos/lattice#188 is released.
        !isLive && 'border-transparent bg-informative/5 text-informative',
        isLive && isDeactivated && 'opacity-50',
      )}
      data-testid={dataTestId}
    >
      {isLive ? t('common.assetStage.live') : t('common.assetStage.draft')}
    </Badge>
  );
}

/**
 * Props for {@link AssetVersionBadges}.
 */
export interface IAssetVersionBadgesProps {
  /** Never published, live, or live with a draft in progress. */
  state: 'unpublished' | 'live' | 'live-with-draft';
  /** Holds the badges' place until the versions behind them are known. */
  isLoading?: boolean;
}

/**
 * The versions an asset has, as {@link AssetVersionBadge}s: Live once
 * published, plus Draft while one is in progress. For the workspace header on
 * Activity, where there's no document to switch versions.
 */
export function AssetVersionBadges({
  state,
  isLoading,
}: IAssetVersionBadgesProps) {
  if (isLoading) {
    return (
      <Skeleton
        className="ml-1 h-5 w-24"
        data-testid="asset-version-badges-skeleton"
      />
    );
  }

  return (
    <Flex
      align="center"
      className="shrink-0"
      data-testid="asset-version-badges"
    >
      {state !== 'unpublished' && (
        <AssetVersionBadge value="live" data-testid="asset-stage-live" />
      )}
      {state !== 'live' && (
        <AssetVersionBadge value="draft" data-testid="asset-stage-draft" />
      )}
    </Flex>
  );
}
